import { useEffect, useState } from "react";
import { KeyRound, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMaster } from "@/lib/useMaster";
import { formatDate, ROLE_LABELS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const selectCls = "h-10 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600";
const emptyForm = { name: "", email: "", password: "", role: "admin_gudang", warehouse_ids: [] };

export default function Pengguna() {
  const { user } = useAuth();
  const { warehouses, warehouseMap } = useMaster();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");

  const load = () => {
    api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
  };

  useEffect(load, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, warehouse_ids: u.warehouse_ids || [] });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/auth/users/${editing.id}`, { name: form.name, role: form.role, warehouse_ids: form.warehouse_ids });
        toast.success("User updated");
      } else {
        await api.post("/auth/users", form);
        toast.success("User added");
      }
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const doReset = async () => {
    setSaving(true);
    try {
      await api.put(`/auth/users/${resetTarget.id}/password`, { password: resetPw });
      toast.success(`Password reset for ${resetTarget.name}`);
      setResetTarget(null);
      setResetPw("");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user "${u.name}"?`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="pengguna-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Users</h1>
          <p className="mt-1 text-sm text-zinc-500">Manage warehouse admin, store admin, and branch manager accounts.</p>
        </div>
        <Button data-testid="btn-add-user" onClick={openCreate} className="rounded-sm bg-blue-600 text-white transition-transform hover:-translate-y-[1px] hover:bg-blue-700">
          <Plus className="mr-2 h-4 w-4" /> Add User
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
        <table className="w-full text-sm" data-testid="user-table">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">In Charge</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-2.5 font-medium text-zinc-900">{u.name}</td>
                <td className="px-4 py-2.5 text-zinc-500">{u.email}</td>
                <td className="px-4 py-2.5">
                  <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${u.role === "owner" ? "border-zinc-800 bg-zinc-900 text-white" : u.role === "admin_gudang" ? "border-blue-200 bg-blue-50 text-blue-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-zinc-500" data-testid={`user-incharge-${u.email}`}>
                  {u.warehouse_ids?.length
                    ? u.warehouse_ids.map((id) => warehouseMap[id]?.code || "?").join(", ")
                    : "All warehouses"}
                </td>
                <td className="px-4 py-2.5 text-zinc-500">{formatDate(u.created_at)}</td>
                <td className="px-4 py-2.5 text-right">
                  <button data-testid={`btn-edit-user-${u.email}`} onClick={() => openEdit(u)} className="mr-1 rounded-sm p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-blue-600" title="Edit user">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button data-testid={`btn-reset-user-${u.email}`} onClick={() => { setResetTarget(u); setResetPw(""); }} className="mr-1 rounded-sm p-1.5 text-zinc-400 transition-colors hover:bg-amber-50 hover:text-amber-600" title="Reset password">
                    <KeyRound className="h-4 w-4" />
                  </button>
                  {u.id !== user.id && (
                    <button data-testid={`btn-delete-user-${u.email}`} onClick={() => remove(u)} className="rounded-sm p-1.5 text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit User" : "Add User"}</DialogTitle>
            <DialogDescription className="sr-only">User creation form</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input data-testid="user-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm border-zinc-300" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input data-testid="user-form-email" type="email" required disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm border-zinc-300" />
            </div>
            {!editing && (
              <div className="space-y-2">
                <Label>Password</Label>
                <Input data-testid="user-form-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm border-zinc-300" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Role</Label>
              <select data-testid="user-form-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={selectCls}>
                <option value="admin_gudang">Warehouse Admin</option>
                <option value="admin_toko">Store Admin</option>
                <option value="branch_manager">Branch Manager</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>In Charge of Warehouses</Label>
              <p className="text-xs text-zinc-500">Leave all unchecked for full access to every warehouse.</p>
              <div className="space-y-1.5" data-testid="user-form-warehouses">
                {warehouses.map((w) => (
                  <label key={w.id} className="flex items-center gap-2 text-sm text-zinc-700">
                    <input
                      type="checkbox"
                      data-testid={`user-form-wh-${w.code}`}
                      checked={form.warehouse_ids.includes(w.id)}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          warehouse_ids: e.target.checked
                            ? [...form.warehouse_ids, w.id]
                            : form.warehouse_ids.filter((x) => x !== w.id),
                        })
                      }
                      className="h-4 w-4 accent-blue-600"
                    />
                    {`${w.name} (${w.code})`}
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="user-form-submit" type="submit" disabled={saving} className="rounded-sm bg-blue-600 text-white hover:bg-blue-700">
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset Password — {resetTarget?.name}</DialogTitle>
            <DialogDescription className="sr-only">Set a new password for this user</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>New Password</Label>
            <Input data-testid="reset-password-input" type="password" minLength={6} value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Min. 6 characters" className="rounded-sm border-zinc-300" />
          </div>
          <DialogFooter>
            <Button data-testid="reset-password-submit" onClick={doReset} disabled={saving || resetPw.length < 6} className="rounded-sm bg-blue-600 text-white hover:bg-blue-700">
              {saving ? "Saving..." : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
